import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// Owns the user feedback feature on the client: whether the floating button is shown (the
// server-controlled runtime flag), the popup's open/submit state, the anonymous submit flow,
// and the admin on/off toggle. Mirrors the other singleton services: shared state lives here
// and the feedback-widget / admin-menu components bind to it directly.
@singleton()
export class FeedbackService {
  private readonly http: HttpClient = resolve(HttpClient);

  // Server-controlled runtime flag. Hidden until loadConfig() resolves so the button never
  // flashes before we know the feature is on.
  enabled: boolean = false;

  // Popup state.
  open: boolean = false;
  comment: string = '';
  email: string = '';
  submitting: boolean = false;
  submitted: boolean = false;
  error: string | null = null;

  // Admin toggle in-flight guard.
  savingEnabled: boolean = false;

  // Loads the current feature state so the widget can show or hide its button. Safe to call
  // unconditionally on startup; failures leave the feature hidden.
  async loadConfig(): Promise<void> {
    try {
      const response = await this.http.get('/api/feedback/config');
      if (!response.ok) return;
      const data = await response.json();
      this.enabled = data.enabled === true;
    } catch {
      // Non-fatal: leave the feature hidden.
      this.enabled = false;
    }
  }

  openPopup(): void {
    this.error = null;
    this.submitted = false;
    this.open = true;
  }

  closePopup(): void {
    this.open = false;
    this.error = null;
    // Keep the typed comment only while the popup was open; reset after a successful send.
    if (this.submitted) {
      this.comment = '';
      this.email = '';
      this.submitted = false;
    }
  }

  // Submits the comment to the server, which emails it to the configured admin address. Any
  // visitor may submit (CSRF-protected like the admin login), so no auth check is needed.
  async submit(): Promise<void> {
    if (this.submitting) return;
    const comment = this.comment.trim();
    if (!comment) {
      this.error = 'Please enter a comment before submitting.';
      return;
    }
    this.submitting = true;
    this.error = null;
    try {
      const response = await this.http.postWithCsrf('/api/feedback', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment, email: this.email.trim() || null })
      });
      if (response.ok) {
        this.submitted = true;
        this.comment = '';
        this.email = '';
        return;
      }
      // A 401 means the session expired (or was never signed in) while the popup was open —
      // the submit endpoint requires a registered user. Give a clear sign-in prompt instead
      // of a raw HTTP error.
      if (response.status === 401) {
        this.error = 'Please sign in to send feedback.';
        return;
      }
      // Both validation errors ({error}) and Problem responses ({detail}) carry a message.
      let message = `Could not send your feedback (HTTP ${response.status}).`;
      try {
        const data = await response.json();
        message = data.error ?? data.detail ?? message;
      } catch {
        // Keep the generic message when the body is not JSON.
      }
      this.error = message;
    } catch (e: any) {
      this.error = `Could not send your feedback: ${e.message}`;
    } finally {
      this.submitting = false;
    }
  }

  // Admin-only: turns the feature on or off for everyone and reflects the new state locally so
  // the floating button appears/disappears immediately. Returns silently on failure.
  async setEnabled(enabled: boolean): Promise<void> {
    if (this.savingEnabled) return;
    this.savingEnabled = true;
    try {
      const response = await this.http.sendWithCsrf('PUT', '/api/feedback/config', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      if (response.ok) {
        this.enabled = enabled;
        if (!enabled) this.open = false;
      }
    } catch {
      // Non-fatal: leave the previous state in place.
    } finally {
      this.savingEnabled = false;
    }
  }
}
